import { isNull } from 'lodash';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { shopsApi, trustedCustomersApi } from 'api';
import {
  CustomersFormSection,
  EntityDetailsPage,
  FormControls,
  ImportListDialog,
  ShopSelect,
} from 'components';
import { NEW_ID } from 'constants/common.constants';
import { ROUTE_PATH } from 'constants/routes';
import { QueryKey } from 'enums';
import {
  CUSTOMER_FIELDS,
  useCustomersForm,
  useShopsQuery,
  useUser,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { TrustedCustomer } from 'types';

type FormValues = {
  shopId: string;
  customers: { customerId: string; customerIp?: string }[];
};

const FIELDS = {
  SHOP_ID: 'shopId' as const,
  CUSTOMERS: 'customers' as const,
};

export const TrustedCustomerDetailsPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'features.trusted_customers',
  });
  const { t: tCommon } = useTranslation();
  const { role, isAdmin, isTechOperator } = useUser();
  const { id } = useParams();

  const [importField, setImportField] = useState<
    (typeof CUSTOMER_FIELDS)[keyof typeof CUSTOMER_FIELDS] | null
  >(null);

  const isNew = useMemo(() => id === NEW_ID, [id]);

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );

  const listUrl = useMemo(() => {
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.TRUSTED_CUSTOMERS;
    } else if (isAdmin) {
      return ROUTE_PATH.ADMIN.TRUSTED_CUSTOMERS;
    }
  }, [isAdmin, isTechOperator]);

  const initialValues: FormValues = useMemo(
    () => ({
      shopId: '',
      customers: [],
    }),
    [],
  );

  const initialValuesSelect = useCallback(
    (item: TrustedCustomer): FormValues => ({
      shopId: item.shopId,
      customers: item.customers || [],
    }),
    [],
  );

  const {
    customersSchema,
    renderCustomerItem,
    handleImportIdConfirm,
    handleImportIpConfirm,
    handleError,
  } = useCustomersForm({
    allowToSetIP: true,
    onClose: () => setImportField(null),
  });

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        shopId: Yup.string().required(tCommon('errors.required')),
        customers: customersSchema,
      }),
    [customersSchema, tCommon],
  );

  const handleBeforeUpdateMutation = useCallback(
    ({ id, data }: { id: string; data: FormValues }) => {
      const { shopId: _, ...rest } = data;
      return { id, data: rest };
    },
    [],
  );

  return (
    <EntityDetailsPage
      id={id!}
      api={trustedCustomersApi}
      apiAsRole={false}
      listUrl={listUrl}
      title={t(isNew ? 'add.title' : 'edit.title')}
      initialValues={initialValues}
      initialValuesSelect={initialValuesSelect}
      queryKey={QueryKey.TrustedCustomers}
      validationSchema={validationSchema}
      onBeforeUpdateMutation={handleBeforeUpdateMutation}
      onError={handleError}
      formClassName="tw-max-w-xl"
    >
      {({ formik }) => (
        <Fragment>
          <FormControls>
            <ShopSelect
              name={FIELDS.SHOP_ID}
              shops={queryResultShops.data}
              required
              disabled={!isNew}
            />
            <CustomersFormSection
              name={FIELDS.CUSTOMERS}
              label={t('fields.customers')}
              addItemLabel={t('buttons.add_customer')}
              defaultValue={{ customerId: '', customerIp: '' }}
              ItemComponent={renderCustomerItem}
              importButtons={[
                {
                  label: t('buttons.import_id'),
                  onClick: () => setImportField(CUSTOMER_FIELDS.ID),
                },
                {
                  label: t('buttons.import_ip'),
                  onClick: () => setImportField(CUSTOMER_FIELDS.IP),
                },
              ]}
            />
          </FormControls>
          <ImportListDialog
            open={!isNull(importField)}
            inputLabel={
              importField === CUSTOMER_FIELDS.IP ? t('fields.ip') : undefined
            }
            onClose={() => setImportField(null)}
            onConfirm={(values) =>
              (importField === CUSTOMER_FIELDS.IP
                ? handleImportIpConfirm
                : handleImportIdConfirm)(
                formik.setFieldValue,
                formik.setFieldTouched,
                formik.values.customers,
                values,
              )
            }
          />
        </Fragment>
      )}
    </EntityDetailsPage>
  );
};
